import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageSEO from '@/components/feature/PageSEO';

const FORM_URL = 'https://readdy.ai/api/form/d9qu0snnahb7382ovfug';

export default function AboutPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypotVal = (fd.get('company_alt') as string || '').trim();
    if (honeypotVal) {
      setFormStatus('success');
      setFormData({ name: '', email: '', message: '' });
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const params = new URLSearchParams();
      fd.forEach((val, key) => {
        if (key !== 'company_alt') {
          params.append(key, val as string);
        }
      });

      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* not json */ }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const msg =
          (parsed?.meta as Record<string, string>)?.message ||
          (parsed?.meta as Record<string, string>)?.detail ||
          (parsed?.message as string) ||
          '提交失败，请稍后重试';
        if (typeof msg === 'string' && (msg.includes('spam') || msg.includes('form data is spam'))) {
          setFormStatus('success');
          setFormData({ name: '', email: '', message: '' });
          return;
        }
        setFormStatus('error');
        setFormError(msg as string);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '黄油推荐',
    url: 'https://huangyou-tuijian.com/',
    description: '精选高品质黄油完整指南，汇聚市面主流与小众优质黄油品牌评测与推荐。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'hello@huangyou-tuijian.com',
    },
  };

  return (
    <>
      <PageSEO
        title="关于我们"
        description="黄油推荐是一个专注于黄油评测与选购指南的专业内容平台，汇聚烘焙爱好者、厨师和家庭用户，提供客观、专业的黄油推荐。"
        keywords="黄油推荐,关于我们,黄油评测,黄油选购"
        canonicalPath="/about"
        schema={orgSchema}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { name: '首页', path: '/' },
            { name: '关于我们' },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
              关于黄油推荐
            </h1>

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
              <div>
                <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
                  我们的使命
                </h2>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  黄油推荐诞生于2024年，由一群热爱烘焙和美食的黄油爱好者创立。我们发现很多人在选购黄油时面临困惑——品牌太多、信息太杂、不知道哪款真正适合自己。
                </p>
                <p className="mt-3 text-sm text-foreground-700 leading-relaxed">
                  于是我们决定做一个<strong>专业、客观、易懂</strong>的黄油评测平台，用真实的横向对比、深入的风味分析、实用的选购指南，帮助每一位烘焙爱好者和家庭用户找到最适合自己的那一块黄油。
                </p>

                <h2 className="font-heading text-lg font-semibold text-foreground-900 mt-8 mb-4">
                  我们的评测标准
                </h2>
                <ul className="space-y-2 text-sm text-foreground-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-primary-500 mt-0.5"></i>
                    <span><strong>风味：</strong>由5位专业测评人盲品打分，评估奶香、发酵香气和余味</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-primary-500 mt-0.5"></i>
                    <span><strong>口感：</strong>评估入口即化程度、细腻度和整体顺滑感</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-primary-500 mt-0.5"></i>
                    <span><strong>烘焙表现：</strong>在曲奇、蛋糕和面包三种场景中实测表现</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-primary-500 mt-0.5"></i>
                    <span><strong>性价比：</strong>结合价格和品质综合评估</span>
                  </li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
                  用户口碑
                </h2>

                <div className="flex flex-col gap-4">
                  {[
                    {
                      name: '烘焙达人小王',
                      text: '自从看了黄油推荐的法国黄油评测，我终于搞清楚了伊斯尼和总统的区别！做可颂用伊斯尼，日常用总统，方案太实用了。',
                      rating: 5,
                    },
                    {
                      name: '家庭主妇李女士',
                      text: '之前买黄油总是纠结，现在每次买之前都来黄油推荐看评测，帮了大忙。特别推荐那篇家用黄油选购指南！',
                      rating: 5,
                    },
                    {
                      name: '面包店主老张',
                      text: '作为开面包店的，黄油的品质直接决定成品口感。黄油推荐的品牌对比给我提供了很好的参考，发酵黄油那篇评测尤其专业。',
                      rating: 5,
                    },
                    {
                      name: '烘焙新手小陈',
                      text: '刚入门烘焙时完全不知道该买什么黄油，黄油推荐的选购指南从入门到进阶讲得很清楚。现在已经能根据自己的需求选黄油了！',
                      rating: 4,
                    },
                  ].map((review, i) => (
                    <div
                      key={i}
                      className="bg-background-100 rounded-lg p-4"
                    >
                      <div className="flex items-center gap-1 mb-1">
                        {Array.from({ length: 5 }).map((_, j) => (
                          <i
                            key={j}
                            className={`text-xs ${
                              j < review.rating
                                ? 'ri-star-fill text-primary-500'
                                : 'ri-star-line text-foreground-300'
                            }`}
                          ></i>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed">
                        &ldquo;{review.text}&rdquo;
                      </p>
                      <p className="mt-2 text-xs text-foreground-500 font-medium">
                        — {review.name}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-12 border-t border-background-200/70 pt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-900 text-center mb-2">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600 text-center mb-8">
                有黄油相关问题？欢迎给我们留言
              </p>

              <div className="max-w-lg mx-auto">
                {formStatus === 'success' ? (
                  <div className="text-center py-10">
                    <span className="w-14 h-14 flex items-center justify-center mx-auto rounded-full bg-primary-100 text-primary-500">
                      <i className="ri-check-line text-2xl"></i>
                    </span>
                    <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">
                      留言已发送！
                    </h3>
                    <p className="mt-2 text-sm text-foreground-600">
                      感谢你的留言，我们会尽快回复。
                    </p>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-4"
                  >
                    <div className="honeypot-wrapper">
                      <input
                        type="text"
                        name="company_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        id="name"
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                        placeholder="你的名字"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        maxLength={500}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors resize-none"
                        placeholder="请输入你的留言（500字以内）"
                      ></textarea>
                      <p className="mt-1 text-xs text-foreground-400 text-right">
                        {formData.message.length}/500
                      </p>
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-2.5 bg-primary-500 text-white font-medium text-sm rounded-lg hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap"
                    >
                      {formStatus === 'loading' ? '发送中...' : '发送留言'}
                    </button>
                    {formStatus === 'error' && (
                      <p className="text-xs text-accent-500 text-center">{formError}</p>
                    )}
                  </form>
                )}

                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-foreground-600">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-mail-line text-primary-500"></i>
                    hello@huangyou-tuijian.com
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-wechat-line text-primary-500"></i>
                    微信公众号：黄油推荐
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}